import { useEffect, useMemo, useState } from "react";
import { Box, Typography, OutlinedInput, FormControl, Button, Modal } from "@mui/material";
import SettingTitle from "../component/SettingTitle";
import theme from "@/theme";
import { useLocation, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useGetUserByUidQuery } from "@/store/api/userApi";
import {
  useChangeEmailMutation,
  useConfirmChangeEmailMutation,
  useResendChangeEmailMutation,
  useVerifyChangeEmailMutation,
} from "@/store/api/authApi";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import OtpFormModal from "@/components/UI/modal/OtpFormModal/OtpFormModal";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ChangeEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [serverError, setServerError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);

  const { user: authUser } = useAppSelector((state) => state.auth);
  if (!authUser) return <Navigate to="/" replace />;

  const [changeEmail] = useChangeEmailMutation();
  const [verifyChangeEmail] = useVerifyChangeEmailMutation();
  const [confirmChangeEmail] = useConfirmChangeEmailMutation();
  const [resendChangeEmail] = useResendChangeEmailMutation();

  const { data: user } = useGetUserByUidQuery(authUser.uid, {
    skip: !authUser?.uid,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [email, setEmail] = useState(location.state?.email || user?.email || "");

  const queryType = searchParams.get("type");
  const token = searchParams.get("token");
  const showOtpModal = queryType === "change-email" && !!token;

  const [changeEmailData, setChangeEmailData] = useState<{
    message: string;
    maskedEmail: string;
    requestUid: string;
  } | null>(null);

  useEffect(() => {
    if (token && !changeEmailData) {
      const confirmEmail = async () => {
        try {
          const res = await confirmChangeEmail({ token }).unwrap();
          setChangeEmailData({
            message: res.message,
            maskedEmail: res.maskedNewEmail,
            requestUid: res.requestUid,
          });
        } catch (e: any) {
          console.error("Confirm change email failed", e);
          setSearchParams({});
          setToast({
            message: e.data?.message || "Failed to change email",
            success: false,
          });
          setTimeout(() => {
            navigate("/settings/account/information");
          }, 4000);
        }
      };

      confirmEmail();
    }
  }, [token]);

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user?.email]);

  useEffect(() => {
    if (location.state?.message) {
      setToast({
        message: location.state.message,
        success: location.state.success ?? true,
      });

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const isValid = useMemo(() => {
    return emailRegex.test(email) && email !== user?.email;
  }, [email, user?.email]);

  const handleSave = async () => {
    if (isLoading) return;
    setServerError(null);
    setIsLoading(true);

    try {
      const res = await changeEmail({ newEmail: email }).unwrap();

      setToast({
        message: `${res.message} (${res.maskedEmail})`,
        success: true,
      });
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || "Something went wrong";
      setServerError(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setServerError(null);
    setEmail(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  const handleChangeEmailOtp = async (otpCode: string) => {
    if (!changeEmailData?.requestUid) return;

    try {
      await verifyChangeEmail({
        requestUid: changeEmailData.requestUid,
        otp: otpCode,
      }).unwrap();

      setTimeout(() => {
        navigate("/settings/account/information", {
          state: { message: "The email has been successfully changed.", success: true },
        });
      }, 2000);
    } catch (e) {
      console.error("Verify change email failed", e);
      throw e;
    }
  };

  const handleResendChangeEmail = async () => {
    if (!changeEmailData?.requestUid) return;

    try {
      const res = await resendChangeEmail({ requestUid: changeEmailData?.requestUid! }).unwrap();
      setChangeEmailData((prev) =>
        prev ? { ...prev, maskedEmail: res.maskedNewEmail, message: res.message } : null
      );
      setToast({
        message: res.message,
        success: true,
      });
    } catch (e: any) {
      console.error("Resend change email failed", e);
      setToast({
        message: e.data.message,
        success: false,
      });
    }
  };

  const handleCloseOtpModal = () => {
    setSearchParams({});
  };

  return (
    <>
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
          durationMs={6000}
        />
      )}

      <Modal
        open={showOtpModal}
        onClose={handleCloseOtpModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <OtpFormModal
          email={changeEmailData?.maskedEmail || "your new email"}
          onSubmit={handleChangeEmailOtp}
          onResend={handleResendChangeEmail}
          onHeaderClick={handleCloseOtpModal}
        />
      </Modal>

      <SettingTitle title="Change email" back isActive={isValid} />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          px: "16px",
          py: "10px",
        }}
      >
        <input type="text" autoComplete="email" style={{ display: "none" }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "15px",
              lineHeight: "24px",
              color: "#292929",
              fontStyle: "Medium",
              letterSpacing: 0,
            }}
          >
            Email
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
            <FormControl fullWidth error={!!serverError}>
              <OutlinedInput
                value={email}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your email"
                type="text"
                error={!!serverError}
                sx={{
                  width: "100%",
                  height: "50px",
                  borderRadius: "999px",
                  maxWidth: "408px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E4E7E9" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C6CACE" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.custom.mainBlue,
                    borderWidth: "1px",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "22px",
                    letterSpacing: 0,
                    color: "#C6CACE",
                    opacity: 1,
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "15px",
                    lineHeight: "20px",
                    letterSpacing: "-0.02em",
                    color: "#383C40",
                    padding: "12px 16px",
                  },
                  ...(serverError && {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E62A3B",
                    },
                  }),
                }}
                autoComplete="off"
                name="changeEmail"
                id="changeEmail"
              />
              {serverError && (
                <Typography
                  sx={{
                    color: "#E62A3B",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "16px",
                    mt: 0.5,
                  }}
                >
                  {serverError}
                </Typography>
              )}
            </FormControl>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                bgcolor: isValid ? theme.palette.custom.mainBlue : "#8BC5F4",
                borderRadius: "28px",
                minWidth: "110px",
                height: "50px",
                color: "#fff",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 500,
                lineHeight: "22px",
                padding: "14px 24px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: isValid ? theme.palette.custom.mainBlue : "#8BC5F4",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#8BC5F4",
                  color: "#FFFFFF",
                  opacity: 1,
                },
              }}
              disabled={!isValid}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ChangeEmail;
